import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import Select from 'react-select';
import Form from '../form/Form';
import { cfpFieldGroups } from '../field_group/CFPFields';
import FieldGroup from '../field_group/FieldGroup';
import { requiredValidator } from '../field_group/validators';
import { getErrorValue } from '../../utils/utility';
import { listVRFIndoor } from '../../redux/vrf/vrf_indoor_actions';
import { listACBrand } from '../../redux/references/ac_actions';
import VRFSeerBadge from './VRFSeerBadge';

// FRS 4.2.1 - VRF Outdoor Unit form (2 tabs: spec + reactive computed)
export const OUTDOOR_TYPES = [
  { id: 'air', name: 'Air-Cooled' },
  { id: 'water', name: 'Water-Cooled' },
];

const numOrPending = (v, t) => (v === null || v === undefined ? t('pending (after zone placement)') : v);

export class VRFOutdoorDetail extends React.Component {
  constructor(props) {
    super(props);
    this.formRef = undefined;
    this.state = {
      selectedIndoorIds: (props.outdoor.indoor_types || []).map(i => i.id),
    };
  }

  componentDidMount() {
    this.props.listIndoor();
    this.props.listBrand();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.outdoor !== this.props.outdoor) {
      this.setState({ selectedIndoorIds: (nextProps.outdoor.indoor_types || []).map(i => i.id) });
    }
  }

  renderOption(item, prefix) {
    return (
      <option key={prefix ? `${prefix}${item.id}` : item.id} value={item.id}>
        {item.name}
      </option>
    );
  }

  getData() {
    return {
      ...this.formRef.getFormData([]),
      indoorTypeIds: this.state.selectedIndoorIds,
    };
  }

  render() {
    const {
      name, code, type, brand_id, q_outdoor, p_input, seer, test_standard,
      q_net, p_total, cop, computed_seer, connection_ratio, seer_rating,
      cfp_value, cfp_unit, description, image,
    } = this.props.outdoor;

    const { t, disabled, fieldErrors, indoorOptions, ...otherAttr } = this.props;
    const customAttr = { disabled };
    const indoorSelectOptions = indoorOptions.map(i => ({ value: i.id, label: i.name }));

    // ใช้ปุ่ม toggle เอง (ไม่ใช้ react-bootstrap Tabs) เพราะ Tabs ไม่ render children ตรงๆ
    // ทำให้ Form.deepMap หา FieldGroup ไม่เจอ + remount ซ้ำ → infinite setState loop
    const activeTab = this.state.activeTab || 'spec';
    return (
      <div>
        <ul className="nav nav-tabs" style={{ marginBottom: 15 }}>
          <li className={activeTab === 'spec' ? 'active' : ''}>
            <a href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'spec' }); }}>
              {t('specification')}
            </a>
          </li>
          <li className={activeTab === 'computed' ? 'active' : ''}>
            <a href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'computed' }); }}>
              {t('calculation result')}
            </a>
          </li>
        </ul>
        <div style={{ display: activeTab === 'spec' ? 'block' : 'none' }}>
          <Form
            image={image}
            {...otherAttr}
            ref={ref => (this.formRef = ref)}
            errorMessage={getErrorValue(fieldErrors, 'detail')}
          >
            <FieldGroup
              id="vrf-outdoor-name" field="name" type="text" label={t('name')}
              value={name} validators={[requiredValidator]}
              errorMessage={getErrorValue(fieldErrors, 'name')} {...customAttr}
            />
            <FieldGroup
              id="vrf-outdoor-code" field="code" type="text" label={t('code')}
              value={code} errorMessage={getErrorValue(fieldErrors, 'code')} {...customAttr}
            />
            <FieldGroup
              id="vrf-outdoor-type" field="typeId" componentClass="select" label={t('type')}
              value={type} defaultDirty={true} {...customAttr}
            >
              {OUTDOOR_TYPES.map(item => this.renderOption(item, 'vrfot'))}
            </FieldGroup>
            <FieldGroup
              id="vrf-outdoor-brand" field="brandId" componentClass="select" label={t('brand')}
              value={brand_id} defaultDirty={true} {...customAttr}
            >
              {this.props.brands.map(brand => this.renderOption(brand, 'vrfb'))}
            </FieldGroup>
            <FieldGroup
              id="vrf-outdoor-q" field="qOutdoor" type="number" label={t('cooling capacity q_outdoor (W)')}
              placeholder="45000" value={q_outdoor} validators={[requiredValidator]}
              errorMessage={getErrorValue(fieldErrors, 'q_outdoor')} {...customAttr}
            />
            <FieldGroup
              id="vrf-outdoor-p" field="pInput" type="number" label={t('power input p_input (W)')}
              placeholder="12000" value={p_input} validators={[requiredValidator]}
              errorMessage={getErrorValue(fieldErrors, 'p_input')} {...customAttr}
            />
            <FieldGroup
              id="vrf-outdoor-seer" field="seer" type="number" label={t('SEER (manufacturer)')}
              placeholder="20" value={seer}
              errorMessage={getErrorValue(fieldErrors, 'seer')} {...customAttr}
            />
            <FieldGroup
              id="vrf-outdoor-test-standard" field="testStandard" type="text" label={t('test standard')}
              value={test_standard} {...customAttr}
            />
            <div className="form-group">
              <label>{t('linked indoor types')}</label>
              <Select
                isMulti
                classNamePrefix="react-select"
                isDisabled={disabled}
                value={indoorSelectOptions.filter(o => this.state.selectedIndoorIds.includes(o.value))}
                options={indoorSelectOptions}
                onChange={selected => this.setState({ selectedIndoorIds: (selected || []).map(s => s.value) })}
                placeholder={t('select indoor types')}
              />
            </div>
            {cfpFieldGroups({ cfp_value, cfp_unit, disabled, fieldErrors, t })}
            <FieldGroup
              id="vrf-outdoor-description" field="description" componentClass="textarea"
              label={t('description')} value={description} {...customAttr}
            />
          </Form>
        </div>
        <div style={{ display: activeTab === 'computed' ? 'block' : 'none', padding: 20 }}>
          <p style={{ color: '#888' }}>
            {t('q_net/cop are computed after placing this outdoor in a zone (indoor count needed)')}
          </p>
          <table className="table">
            <tbody>
              <tr><th>{t('net cooling capacity q_net (W)')}</th><td>{numOrPending(q_net, t)}</td></tr>
              <tr><th>{t('total power input p_total (W)')}</th><td>{numOrPending(p_total, t)}</td></tr>
              <tr><th>{t('COP')}</th><td>{numOrPending(cop, t)}</td></tr>
              <tr><th>{t('computed SEER (Btu/h/W)')}</th><td>{numOrPending(computed_seer, t)}</td></tr>
              <tr><th>{t('connection ratio (%)')}</th><td>{numOrPending(connection_ratio, t)}</td></tr>
              <tr>
                <th>{t('EGAT efficiency level (from SEER)')}</th>
                <td><VRFSeerBadge code={seer_rating} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

VRFOutdoorDetail.defaultProps = {
  outdoor: {},
  indoorOptions: [],
};

const mapStateToProps = state => ({
  brands: state.reference.ac.brandList,
  indoorOptions: state.vrfIndoor.orderedIds.map(id => state.vrfIndoor.objects[id]),
});

const mapDispatchToProps = dispatch => ({
  listIndoor: () => dispatch(listVRFIndoor(200, 0, null, null, null)),
  listBrand: () => dispatch(listACBrand()),
});

export default translate('translations', { withRef: true })(
  connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(VRFOutdoorDetail),
);



// WEBPACK FOOTER //
// ./src/components/vrf/VRFOutdoorDetail.js